import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowLeft, MailCheck } from 'lucide-react'

import { useAuth } from '@/shared/auth'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card'

export function EmailVerificationScreen() {
    const { t } = useTranslation()
    const { user, sendEmailVerification, signOut } = useAuth()
    const [isResending, setIsResending] = useState(false)
    const [resendSuccess, setResendSuccess] = useState(false)
    const [isSigningOut, setIsSigningOut] = useState(false)
    const email = user?.email

    const handleResendEmail = async () => {
        setIsResending(true)
        setResendSuccess(false)
        try {
            await sendEmailVerification(email ?? undefined)
            setResendSuccess(true)
            setTimeout(() => setResendSuccess(false), 5000)
        } catch (error) {
            console.error('Error resending verification email:', error)
        } finally {
            setIsResending(false)
        }
    }

    const handleBackToLogin = async () => {
        setIsSigningOut(true)
        try {
            await signOut()
            // The auth context will automatically redirect to login
        } catch (error) {
            console.error('Error signing out:', error)
        } finally {
            setIsSigningOut(false)
        }
    }

    return (
        <div className='flex min-h-screen items-center justify-center bg-gray-50 p-4'>
            <Card className='relative w-full max-w-md'>
                <Button
                    onClick={handleBackToLogin}
                    variant='ghost'
                    disabled={isSigningOut}
                    className='absolute left-4 top-4'
                >
                    <ArrowLeft className='mr-2 h-4 w-4' />
                    {isSigningOut
                        ? t('auth.emailVerification.signingOut', 'Cerrando sesión...')
                        : t('auth.emailVerification.backToLogin', 'Volver al login')}
                </Button>
                <CardHeader className='text-center'>
                    <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100'>
                        <MailCheck className='h-8 w-8 text-blue-600' />
                    </div>
                    <CardTitle className='text-2xl'>{t('auth.emailVerification.title', 'Verifica tu Email')}</CardTitle>
                    <CardDescription>
                        {t(
                            'auth.emailVerification.message',
                            'Hemos enviado un email de verificación a la siguiente dirección. Por favor, verifica tu email para continuar con tu reserva.',
                        )}
                    </CardDescription>
                    {email && (
                        <p className='mt-3 break-all text-center text-base font-semibold text-foreground'>{email}</p>
                    )}
                </CardHeader>
                <CardContent className='space-y-4'>
                    <div className='rounded-md bg-blue-50 p-3 text-sm text-blue-800'>
                        {t('auth.emailVerification.checkSpam', 'Si no ves el email, revisa tu carpeta de spam.')}
                    </div>

                    <p className='text-center text-sm text-muted-foreground'>
                        {t(
                            'auth.emailVerification.wrongEmail',
                            'Si este no es tu email, vuelve al login e inicia sesión con la cuenta correcta.',
                        )}
                    </p>

                    {resendSuccess && (
                        <div className='rounded-md bg-green-50 p-3 text-sm text-green-800'>
                            <p className='font-semibold'>{t('auth.emailVerification.resent', 'Email reenviado')}</p>
                            <p>
                                {email
                                    ? t(
                                          'auth.emailVerification.resentMessageWithEmail',
                                          'Hemos reenviado el email de verificación a {{email}}.',
                                          { email },
                                      )
                                    : t(
                                          'auth.emailVerification.resentMessage',
                                          'Hemos reenviado el email de verificación.',
                                      )}
                            </p>
                        </div>
                    )}

                    <div className='flex flex-col gap-3'>
                        <Button onClick={handleResendEmail} variant='outline' disabled={isResending} className='w-full'>
                            {isResending
                                ? t('auth.emailVerification.resending', 'Reenviando...')
                                : t('auth.emailVerification.resend', 'Reenviar email de verificación')}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}
